import React, { useCallback } from "react";

import { useForm } from "react-hook-form-7";
import { Link as RRLink } from "react-router-dom";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Stack,
  Divider,
  Text,
} from "@chakra-ui/react";

import { useUserSendPasswordReset } from "../../../graphql/generated/definitions";

import useTranslations from "../../../hooks/useTranslations";

import LoginLayout from "../layout/LoginLayout";

type ResetPasswordFormInputs = { email: string };

export default function ForgottenPasswordPage() {
  const t = useTranslations();

  const {
    register,
    formState: { errors, isSubmitSuccessful, isValid },
    handleSubmit,
  } = useForm<ResetPasswordFormInputs>({ mode: "onChange" });

  const [userSendPasswordReset] = useUserSendPasswordReset();

  const onSubmit = useCallback(async ({ email }: ResetPasswordFormInputs) => {
    try {
      await userSendPasswordReset({
        variables: {
          email,
          redirectUrl: `${window.location.protocol}//${window.location.host}/reset-password`,
        },
      });
    } catch {
      // Should not append
    }
  }, []);

  return (
    <LoginLayout title={t("header.passwordLost") as string}>
      <Stack spacing="6">
        <Divider color="gray.200" />
        <Text fontWeight="semibold">
          {t("forgottenPassword.fillFormToReset")}
        </Text>
        <form onSubmit={handleSubmit(onSubmit)} id="forgotten-password-form">
          <Stack spacing="5">
            <FormControl
              id="email"
              isRequired
              isInvalid={!!errors?.email?.message}
            >
              <FormLabel>{t("forms.fields.email")}</FormLabel>
              <Input
                size="lg"
                type="email"
                // This looks rather ugly but native form validation block the one made by RHF
                isRequired={false}
                aria-required="true"
                {...register("email", {
                  required: t("forms.errors.required") as string,
                })}
              />
              <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
            </FormControl>
            {isSubmitSuccessful && (
              <Alert
                status="success"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="flex-start"
                id="forgotten-password-form-success"
              >
                <AlertTitle textAlign="start">
                  {t("forgottenPassword.emailSentLabel")}
                </AlertTitle>
                <AlertDescription>
                  {t("forgottenPassword.emailSentDescription")}
                </AlertDescription>
              </Alert>
            )}
            <Stack spacing="6" pt="4">
              <Button
                disabled={!isValid}
                type="submit"
                isFullWidth
                colorScheme="primary"
              >
                {t("forms.actions.send")}
              </Button>
              <Link
                as={RRLink}
                to="/login"
                color="secondary"
                textAlign="center"
                fontSize="sm"
                fontWeight="semibold"
              >
                {t("login.backToLogin")}
              </Link>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </LoginLayout>
  );
}
